/**
 * GatedAction — Button that shows as disabled with an explanation when
 * the user doesn't meet the required conditions.
 *
 * Usage:
 *   <GatedAction
 *     enabled={claimStatus === 'approved'}
 *     reason="Requer vínculo com base oficial"
 *     onClick={() => navigate('/manager/athletes')}
 *     icon={Users}
 *   >
 *     Gerenciar Atletas
 *   </GatedAction>
 *
 * Props:
 *   enabled      - boolean: if false, button is disabled + reason shown
 *   reason       - string: explanation shown below button when disabled
 *   onClick      - function: action when enabled and clicked
 *   href         - string: if provided, renders as <a> link instead of button
 *   icon         - Lucide icon component (optional)
 *   variant      - 'primary' | 'secondary' | 'gold' (default: 'primary')
 *   className    - extra classes
 */
import React from 'react';
import { Lock } from 'lucide-react';
import { VUKZ } from '../../theme/vukzTheme';

const VARIANTS = {
  primary: {
    bg: VUKZ?.primary || '#111827',
    text: '#fff',
    disabledBg: '#e5e7eb',
    disabledText: '#9ca3af',
  },
  secondary: {
    bg: '#fff',
    text: VUKZ?.text || '#111827',
    border: VUKZ?.border || '#e5e7eb',
    disabledBg: '#f9fafb',
    disabledText: '#9ca3af',
  },
  gold: {
    bg: VUKZ?.gold || '#b48c2c',
    text: '#fff',
    disabledBg: '#e5e7eb',
    disabledText: '#9ca3af',
  },
};

const GatedAction = ({
  enabled = true,
  reason,
  onClick,
  href,
  icon: Icon,
  variant = 'primary',
  className = '',
  children,
}) => {
  const v = VARIANTS[variant] || VARIANTS.primary;

  const baseClasses = `
    inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl
    text-sm font-bold transition-all duration-200
    ${className}
  `.trim();

  if (!enabled) {
    return (
      <div className="inline-flex flex-col items-start">
        <div
          className={baseClasses}
          style={{
            backgroundColor: v.disabledBg,
            color: v.disabledText,
            cursor: 'not-allowed',
            opacity: 0.7,
            border: variant === 'secondary' ? `1px solid #e5e7eb` : undefined,
          }}
        >
          <Lock size={14} />
          {children}
        </div>
        {reason && (
          <span className="text-xs mt-1.5 ml-1 flex items-center gap-1"
            style={{ color: '#9ca3af' }}>
            <Lock size={10} /> {reason}
          </span>
        )}
      </div>
    );
  }

  const activeStyle = {
    backgroundColor: v.bg,
    color: v.text,
    border: variant === 'secondary' ? `1px solid ${v.border}` : undefined,
  };

  if (href) {
    return (
      <a href={href} className={`${baseClasses} hover:opacity-90`} style={activeStyle}>
        {Icon && <Icon size={16} />}
        {children}
      </a>
    );
  }

  return (
    <button onClick={onClick} className={`${baseClasses} hover:opacity-90`} style={activeStyle}>
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
};

export default GatedAction;
